import { colorVar } from '@/theme/colorMode/colorVar';
import { fontSizes } from '@/theme/foundations/fontSizes';
import { fontWeights } from '@/theme/foundations/fontWeights';

export const customStyles = {
  control: (base, state) => {
    const hasError = state.selectProps.hasError;
    return {
      ...base,
      padding: state.selectProps?.hasSearchIcon ? '0 12px' : 0,
      fontSize: fontSizes['sm'],
      color: colorVar('secondary-700'),
      borderRadius: '6px',
      border: '2px solid',
      borderColor: hasError
        ? `${colorVar('error-200')} !important`
        : `${colorVar('border-color')} !important`,
      background: state.isDisabled
        ? colorVar('secondary-50')
        : hasError
          ? colorVar('pastel-1-light')
          : colorVar('base-white'),
      boxShadow: hasError ? '0 0 0 2px #E53E3E' : 'none',
      '&:hover': state.isDisabled
        ? {
            cursor: 'not-allowed',
          }
        : hasError
          ? {
              cursor: 'pointer',
              border: '2px solid',
              borderColor: `${colorVar('error-200')} !important`,
              background: colorVar('pastel-1-light'),
              boxShadow: '0 0 0 2px #E53E3E',
              outline: 'none',
            }
          : {
              cursor: 'pointer',
              border: '2px solid',
              borderColor: `${colorVar('primary-500')} !important`,
              boxShadow: '0px 4px 8px rgba(214, 214, 214, 0.15)',
              outline: '4px solid',
              outlineColor: '#6220A30F',
            },
      '&:focus': hasError
        ? {
            background: colorVar('pastel-1-light'),
            borderColor: `${colorVar('error-200')} !important`,
            boxShadow: '0 0 0 2px #E53E3E',
          }
        : {
            background: colorVar('white'),
            borderColor: colorVar('secondary-300'),
          },
    };
  },
  placeholder: (base, state) => ({
    ...base,
    color: state.selectProps.hasError
      ? colorVar('error-100')
      : colorVar('secondary-500'),
  }),
  input: (base) => ({
    ...base,
    color: colorVar('secondary-700'),
  }),
  singleValue: (base, state) => ({
    ...base,
    fontSize: fontSizes['sm'],
    fontWeight: fontWeights['medium'],
    color: state.isDisabled
      ? colorVar('secondary-500')
      : colorVar('secondary-700'),
  }),

  multiValue: (base, state) => ({
    ...base,
    borderRadius: '6px',
    ...base,
    fontSize: fontSizes['sm'],
    fontWeight: fontWeights['medium'],
    color: colorVar('secondary-700'),
    opacity: state.isDisabled ? 0.5 : 1,
    background: colorVar('white'),
    border: '1px solid',
    borderColor: colorVar('border-color'),
    padding: '0 2px',
  }),

  multiValueLabel: (_base) => ({
    fontSize: fontSizes['sm'],
    padding: '4px',
    fontWeight: fontWeights['semiBold'],
    color: colorVar('secondary-700'),
  }),
  multiValueRemove: (base) => ({
    ...base,
    '&:hover': {
      background: colorVar('secondary-50'),
    },
  }),

  menu: (base) => ({
    ...base,
    backgroundColor: colorVar('white'),
    border: '1px solid var(--kula-colors-light-200, #EDEDED)',
    boxShadow: '0px 4px 12px 6px rgba(46, 47, 48, 0.06)',
    padding: '4px 8px',
  }),

  menuList: (base) => ({ ...base, maxHeight: '200px' }),

  option: (base, state) => ({
    ...base,
    fontSize: fontSizes['sm'],
    fontWeight: fontWeights['medium'],
    cursor: 'pointer',
    color: colorVar('secondary-700'),
    background:
      state.isSelected || state.isFocused
        ? colorVar('secondary-50')
        : colorVar('white'),
    '&:hover': {
      background: colorVar('secondary-50'),
    },
    borderRadius: '6px',
  }),

  loadingMessage: (base) => ({
    ...base,
    color: colorVar('secondary-700'),
    padding: 0,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

export const getCustomStyles = (newStyles = []) => {
  const defaultStyles = {
    control: (base, state, newStyles) => {
      return {
        ...base,
        padding: state.selectProps?.hasSearchIcon ? '0 12px' : 0,
        fontSize: fontSizes['sm'],
        borderRadius: '6px',
        background: state.isDisabled
          ? colorVar('secondary-50')
          : state.selectProps.hasError
            ? colorVar('pastel-1-light')
            : colorVar('base-white'),
        border: '1px solid',
        // Error must win over focus/hover so border stays red when invalid.
        borderColor: `${
          state.selectProps.hasError
            ? colorVar('error-200')
            : state.isFocused
              ? colorVar('secondary-300')
              : colorVar('secondary-100')
        } !important`,
        boxShadow: 'none',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        '&:hover': state.isDisabled
          ? {
              cursor: 'not-allowed',
            }
          : state.selectProps.hasError
            ? {
                cursor: 'pointer',
                border: '1px solid',
                borderColor: `${colorVar('error-200')} !important`,
                background: `${colorVar('pastel-1-light')}`,
                boxShadow: 'none',
              }
            : {
                cursor: 'pointer',
                border: '1px solid',
                borderColor: `${colorVar('secondary-300')} !important`,
                background: `${colorVar('secondary-25')}`,
              },
        ...newStyles,
      };
    },

    singleValue: (base, state, newStyles) => ({
      ...base,
      fontSize: fontSizes['sm'],
      fontWeight: fontWeights['medium'],
      color: state.isDisabled
        ? colorVar('secondary-500')
        : colorVar('secondary-700'),
      ...(state.selectProps?.hasIcon && { marginLeft: '8px' }),
      ...newStyles,
    }),

    multiValue: (base, state, newStyles) => {
      return {
        ...base,
        fontSize: fontSizes['sm'],
        fontWeight: fontWeights['medium'],
        color: colorVar('secondary-700'),
        opacity: state.isDisabled ? 0.5 : 1,
        background: colorVar('white'),
        border: '1px solid',
        borderColor: colorVar('border-color'),
        borderRadius: '6px',
        ...newStyles,
      };
    },
    multiValueLabel: (base, state, newStyles) => ({
      fontSize: fontSizes['sm'],
      padding: '4px',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      OverflowWrap: 'anywhere',
      fontWeight: fontWeights['medium'],
      ...newStyles,
    }),
    multiValueRemove: (base, state, newStyles) => ({
      ...base,
      color: colorVar('secondary-700'),
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: colorVar('white'),
        color: colorVar('secondary-700'),
        borderRadius: '6px',
      },
      ...newStyles,
    }),

    menu: (base, state, newStyles) => ({
      ...base,
      backgroundColor: colorVar('white'),
      border: '1px solid ',
      borderColor: colorVar('border-color'),
      boxShadow: '0px 4px 12px 6px rgba(46, 47, 48, 0.06)',
      padding: '4px 8px',
      zIndex: 9999,
      ...newStyles,
    }),

    menuList: (base, state, newStyles) => ({
      ...base,
      maxHeight: '200px',
      ...newStyles,
    }),

    option: (base, state, newStyles) => ({
      ...base,
      fontSize: fontSizes['sm'],
      fontWeight: fontWeights['medium'],
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      opacity: state.isDisabled ? 0.5 : 1,
      color: 'var(--kula-colors-light-text-primary, #6D659D)',
      background:
        state.isSelected || state.isFocused
          ? colorVar('input-fill')
          : colorVar('white'),
      '&:hover': {
        background: colorVar('input-fill'),
      },
      borderRadius: '6px',
      ...newStyles,
    }),

    loadingMessage: (base, state, newStyles) => ({
      ...base,
      padding: 0,
      color: colorVar('secondary-700'),
      ...newStyles,
    }),
    menuPortal: (base, state, newStyles) => ({
      ...base,
      zIndex: 9999,
      ...newStyles,
    }),
    placeholder: (base, state, newStyles) => ({
      ...base,
      fontSize: fontSizes['sm'],
      fontWeight: fontWeights['medium'],
      fontFamily: 'Inter, system-ui, sans-serif',
      paddingTop: '1.125em',
      paddingBottom: '1.125em',
      color: state.selectProps.hasError
        ? colorVar('error-100')
        : colorVar('secondary-500'),
      ...newStyles,
    }),
    valueContainer: (base, state, newStyles) => ({
      ...base,
      minHeight: '44px',
      padding: '4px 8px',
      gap: '4px',
      ...newStyles,
    }),
    input: (base, state, newStyles) => ({
      ...base,
      color: colorVar('secondary-700'),
      ...newStyles,
    }),
    groupHeading: (base, state, newStyles) => ({
      ...base,
      fontWeight: 'semiBold',
      fontSize: fontSizes['lg'],
      color: colorVar('ats-text-primary'),
      ...newStyles,
    }),
    dropdownIndicator: (base, state, newStyles) => ({
      ...base,
      color: state?.isDisabled
        ? colorVar('secondary-500')
        : state.selectProps.hasError
          ? colorVar('error-200')
          : colorVar('secondary-700'),

      '& svg': {
        width: '16px',
        height: '16px',
        strokeWidth: 0.05,
      },
      ...newStyles,
    }),
    indicatorsContainer: (base, state, newStyles) => ({
      ...base,
      alignItems: 'center',
      ...newStyles,
    }),
  };

  if (newStyles?.length === 0) return defaultStyles;

  const styledDefaultStyles = {};

  for (const type in defaultStyles) {
    const styledFunction = defaultStyles[type];
    const relevantNewStyles = newStyles?.find(
      (style) => style?.type === type
    )?.newStyles;
    styledDefaultStyles[type] = (base, state) =>
      styledFunction(base, state, relevantNewStyles);
  }

  return styledDefaultStyles;
};

export const newCustomStyles = {
  control: (base, state) => {
    return {
      ...base,
      padding: state.selectProps?.hasSearchIcon ? '0 12px' : 0,
      fontSize: fontSizes['sm'],
      borderRadius: '6px',
      background: colorVar('white'),
      border: 'none',
      boxShadow: state.selectProps.hasError ? '0 0 0 2px #E53E3E' : 'none',
      borderColor: '#131415',
      '&:hover': state.selectProps.hasError
        ? {
            cursor: 'pointer',
            boxShadow: '0 0 0 2px #E53E3E',
          }
        : {
            cursor: 'pointer',
          },
      '&:focus': {
        background: colorVar('white'),
      },
    };
  },

  multiValue: (base) => ({
    ...base,
    background: '#E0E8E8',
    borderRadius: '6px',
    padding: '0 2px',
  }),

  multiValueLabel: (_base) => ({
    fontSize: fontSizes['sm'],
    padding: '4px',
    fontWeight: fontWeights['semiBold'],
  }),
  multiValueRemove: (base) => ({
    ...base,
    '&:hover': {
      background: '#E0E8E8',
    },
  }),

  menu: (base) => ({
    ...base,
    backgroundColor: colorVar('white'),
    border: '1px solid var(--kula-colors-light-200, #EDEDED)',
    boxShadow: '0px 4px 12px 6px rgba(46, 47, 48, 0.06)',
    padding: '4px 8px',
  }),

  menuList: (base) => ({ ...base, maxHeight: '200px' }),

  option: (base, state) => ({
    ...base,
    fontSize: fontSizes['sm'],
    fontWeight: fontWeights['semiBold'],
    cursor: 'pointer',
    color: 'var(--kula-colors-light-text-primary, #6D659D)',
    background:
      state.isSelected || state.isFocused
        ? colorVar('input-fill')
        : colorVar('white'),
    '&:hover': {
      background: colorVar('input-fill'),
    },
    borderRadius: '6px',
  }),

  loadingMessage: (base) => ({
    ...base,
    color: colorVar('secondary-700'),
    padding: 0,
  }),
};
